// Settings modal: the two due-date threshold knobs (imminentDays /
// soonDays, per design §3.4). Display name is edited via the avatar
// chip, not here.

import type { Settings } from "../generated/Settings";
import { local, localApply } from "../state";
import { keepOverlayAboveKeyboard } from "./modal-viewport";

const DEFAULT_HIDE_DAYS = 62;
const MAX_HIDE_DAYS = 3650;

export function openSettingsModal(): void {
  closeSettings();

  const overlay = document.createElement("div");
  overlay.className = "modal__overlay";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeSettings();
  });

  const dialog = document.createElement("div");
  dialog.className = "modal";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");

  const title = document.createElement("h2");
  title.className = "modal__title";
  title.textContent = "Settings";

  const form = document.createElement("form");
  form.className = "modal__form";

  const imm = document.createElement("input");
  imm.type = "number";
  imm.min = "0";
  imm.max = "365";
  imm.value = String(local.state.settings.imminentDays);
  imm.className = "modal__num-input";

  const soon = document.createElement("input");
  soon.type = "number";
  soon.min = "0";
  soon.max = "365";
  soon.value = String(local.state.settings.soonDays);
  soon.className = "modal__num-input";

  const showCompleted = document.createElement("input");
  showCompleted.type = "checkbox";
  showCompleted.className = "modal__completed-check";
  showCompleted.checked = local.state.settings.showCompleted;

  const curHide = local.state.settings.hideEventsBeyondDays;
  const hideCheck = document.createElement("input");
  hideCheck.type = "checkbox";
  hideCheck.className = "modal__completed-check";
  hideCheck.checked = curHide != null;

  const hideDays = document.createElement("input");
  hideDays.type = "number";
  hideDays.min = "0";
  hideDays.max = String(MAX_HIDE_DAYS);
  hideDays.className = "modal__num-input modal__num-input--inline";
  hideDays.value = String(curHide ?? DEFAULT_HIDE_DAYS);
  hideDays.disabled = !hideCheck.checked;
  hideCheck.addEventListener("change", () => {
    hideDays.disabled = !hideCheck.checked;
  });

  const explain = document.createElement("p");
  explain.className = "modal__hint";
  explain.textContent =
    "Tasks due within imminent days get a pink tint; within soon days, yellow.";

  const error = document.createElement("p");
  error.className = "modal__error";
  error.hidden = true;

  const actions = document.createElement("div");
  actions.className = "modal__actions";

  const save = () => {
    const i = Number(imm.value);
    const s = Number(soon.value);
    if (!Number.isInteger(i) || !Number.isInteger(s)) {
      showError(error, "Imminent and soon must be integers.");
      return;
    }
    if (i < 0 || s < 0 || i > 365 || s > 365 || i > s) {
      showError(error, "Need 0 ≤ imminent ≤ soon ≤ 365.");
      return;
    }
    let hideVal: number | undefined = undefined;
    if (hideCheck.checked) {
      const h = Number(hideDays.value);
      if (!Number.isInteger(h) || h < 0 || h > MAX_HIDE_DAYS) {
        showError(
          error,
          `Hide days must be a whole number between 0 and ${MAX_HIDE_DAYS}.`,
        );
        return;
      }
      hideVal = h;
    }
    const sc = showCompleted.checked;
    if (
      i !== local.state.settings.imminentDays ||
      s !== local.state.settings.soonDays ||
      sc !== local.state.settings.showCompleted ||
      hideVal !== local.state.settings.hideEventsBeyondDays
    ) {
      const settings: Settings = { imminentDays: i, soonDays: s, showCompleted: sc };
      if (hideVal !== undefined) settings.hideEventsBeyondDays = hideVal;
      localApply({ type: "settings", settings });
    }
    closeSettings();
  };

  // Enter in any input runs save (via the submit pathway).
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    save();
  });

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => closeSettings());

  const saveBtn = document.createElement("button");
  saveBtn.type = "submit";
  saveBtn.className = "btn btn--primary";
  saveBtn.textContent = "Save";

  actions.append(cancel, saveBtn);

  form.append(
    field("Imminent days", imm),
    field("Soon days", soon),
    inlineField("Show completed tasks", showCompleted),
    hideEventsField(hideCheck, hideDays),
    explain,
    error,
    actions,
  );

  dialog.append(title, form);
  overlay.append(dialog);
  document.body.append(overlay);
  keepOverlayAboveKeyboard(overlay);
  imm.focus();
  imm.select();
}

function closeSettings(): void {
  for (const ov of document.querySelectorAll(".modal__overlay")) ov.remove();
}

function field(labelText: string, input: HTMLInputElement): HTMLElement {
  const row = document.createElement("label");
  row.className = "modal__row";
  const span = document.createElement("span");
  span.textContent = labelText;
  row.append(span, input);
  return row;
}

/** Single-row variant — checkbox on the left, label text on the right.
 *  Used for boolean toggles where the label-above-input pattern would
 *  look unbalanced. */
function inlineField(labelText: string, input: HTMLInputElement): HTMLElement {
  const row = document.createElement("label");
  row.className = "modal__row modal__row--inline";
  const span = document.createElement("span");
  span.textContent = labelText;
  row.append(input, span);
  return row;
}

/** Composite row: `[✓] Hide events due beyond [__] days in the future`. */
function hideEventsField(
  check: HTMLInputElement,
  days: HTMLInputElement,
): HTMLElement {
  const row = document.createElement("label");
  row.className = "modal__row modal__row--inline modal__row--hide-events";
  const lead = document.createElement("span");
  lead.textContent = "Hide events due beyond";
  const trail = document.createElement("span");
  trail.textContent = "days in the future";
  row.append(check, lead, days, trail);
  return row;
}

function showError(el: HTMLElement, msg: string): void {
  el.textContent = msg;
  el.hidden = false;
}
